import { createApiClient } from '@multaqa/api-client'
import { i18next } from '@multaqa/i18n'
import { recordServerActivity } from './session-activity'

/**
 * The API origin, with no trailing slash and no `/api` prefix.
 *
 * `VITE_API_URL` is the ORIGIN. Every raw-fetch call site in this app appends
 * `/api` to it itself, and so does the typed client below.
 */
export const apiOrigin = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

export function recordSessionActivity(response: Response): void {
  if (response.status !== 401) {
    recordServerActivity()
  }
}

/** The caller's headers in their own shape, plus the reader's language unless already set. */
function withLanguage(headers: HeadersInit | undefined): HeadersInit {
  const language = (i18next.language ?? 'en').split('-')[0] ?? 'en'
  if (headers instanceof Headers || Array.isArray(headers)) {
    const merged = new Headers(headers)
    if (!merged.has('Accept-Language')) merged.set('Accept-Language', language)
    return merged
  }
  const record = { ...(headers ?? {}) }
  const hasLanguage = Object.keys(record).some((key) => key.toLowerCase() === 'accept-language')
  return hasLanguage ? record : { ...record, 'Accept-Language': language }
}

/**
 * A raw fetch to the API (downloads, multipart, endpoints outside the typed
 * client). It sends the reader's language, as the typed client does, so the
 * server writes labels and messages in it; a caller's own header wins.
 */
export async function fetchWithSessionActivity(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, { ...init, headers: withLanguage(init?.headers) })
  recordSessionActivity(response)
  return response
}

/**
 * WHY THE TYPED BASE URL IS NOT SIMPLY `apiOrigin`
 *
 * The API serves three route families at three different depths:
 *
 *   /api/<spec path>        module routes  -> the typed client
 *   /sanctum/csrf-cookie    the CSRF cookie
 *   /login, /logout, ...    Fortify, on the `web` group
 *
 * The generated `paths` in the api-client are relative to the spec's server
 * URL, which already ends in `/api`, so the keys read `/accounting/accounts`.
 * A cross-origin build therefore needs `<origin>/api` for typed calls and a
 * bare `<origin>` for the other two.
 *
 * In development `apiOrigin` is empty and Vite's proxy maps all three families
 * itself, so the paths the client emits do not have to be complete. That is
 * exactly why a wrong production value produced no failing test: no test and
 * no dev session ever built a complete URL. `src/lib/api-origin.test.ts` is
 * the regression guard, and it is the only place that uses a real origin.
 */
export const api = createApiClient({
  baseUrl: apiOrigin === '' ? '' : `${apiOrigin}/api`,
  authOrigin: apiOrigin,
  headers: () => ({ 'Accept-Language': (i18next.language ?? 'en').split('-')[0] ?? 'en' }),

  onResponse: recordSessionActivity,

  onUnauthorized: (error) => {
    // ADR-0033: a support user has no password, so an ended support session
    // goes to its own page, never to the sign-in page.
    if (error.code === 'support_session_ended') {
      window.location.assign('/support/ended')
      return
    }
    // Full navigation rather than a router push: the session is gone, so
    // everything in memory — query cache, stores — should go with it.
    if (window.location.pathname !== '/login') {
      const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`
      window.location.assign(`/login?${new URLSearchParams({ redirect })}`)
    }
  },
})
